import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
  Autocomplete,
  Box,
  Typography,
  Chip,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Alert,
  Paper,
  Stack,
  Collapse,
} from '@mui/material';
import {
  MyLocation as MyLocationIcon,
  Clear as ClearIcon,
  LocationOn as LocationIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  History as HistoryIcon,
  BookmarkBorder as SavedIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { addFavorite, addRecent } from '../../../app/redux/slices/addressBookSlice';
import { fetchAddressSuggestions } from '../utils/addressUtils';
import { COLORS } from '../utils/constants';
import useApiCall from '../../../services/api/useApiCall';
import { buildApiConfig } from '../../../services/api/apiClient';
import { endPointEnums } from '../../../services/api/endpoints';
import { useTranslation } from '@/kit/useTranslation';

/**
 * Modern address input with autocomplete and saved addresses
 * Now with API persistence for saving addresses
 */

// separate wrapper to avoid inline function in JSX (fixes Next/SWC parser bug)
function AutocompletePaperWrapper(boxProps) {
  return (
    <Box
      {...boxProps}
      sx={{
        borderRadius: 3,
        boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
        border: `1px solid ${COLORS.border}`,
        overflow: 'hidden',
        mt: 1,
        backgroundColor: '#ffffff',
        '& .MuiAutocomplete-listbox': {
          backgroundColor: '#ffffff',
          p: 0.5,
        },
      }}
    >
      {boxProps.children}
    </Box>
  );
}

export default function AddressInput({
  label,
  value,
  onChange,
  onUseCurrent,
  showSavedAddresses = true,
}) {
  const dispatch = useDispatch();
  const { makeApiCall } = useApiCall();
  const { t } = useTranslation();
  const { recents, favorites } = useSelector((state) => state.addressBook);

  // inputValue controls what is visible in the text field
  const [inputValue, setInputValue] = useState(
    typeof value === 'string'
      ? value
      : typeof value === 'object'
        ? value.address || value.label || value.formatted_address || ''
        : ''
  );
  // selectedOption keeps track of the suggestion object (if any) chosen by the user
  const [selectedOption, setSelectedOption] = useState(
    typeof value === 'object' && value !== null ? value : null
  );
  const [options, setOptions] = useState([]);
  const [savedAddressesFromApi, setSavedAddressesFromApi] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [expandedSavedAddresses, setExpandedSavedAddresses] = useState(false);
  // Track whether inputValue changed due to user typing vs external/hydration update
  const userTypingRef = useRef(false);
  const [savedMenuAnchor, setSavedMenuAnchor] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Fetch saved addresses from API on component mount
  useEffect(() => {
    const fetchSavedAddresses = async () => {
      setLoadingSaved(true);
      try {
        const config = buildApiConfig(endPointEnums.GET_SAVED_ADDRESSES);
        const response = await makeApiCall(config);
        if (response?.success && Array.isArray(response.data)) {
          setSavedAddressesFromApi(response.data);
        }
      } catch (err) {
        console.error('[AddressInput] Failed to fetch saved addresses:', err);
      } finally {
        setLoadingSaved(false);
      }
    };
    if (showSavedAddresses) {
      fetchSavedAddresses();
    }
  }, [showSavedAddresses, makeApiCall]);

  // Sync with external value changes (string or object)
  useEffect(() => {
    if (typeof value === 'string') {
      if (value !== inputValue) {
        userTypingRef.current = false; // external update, not user typing
        setInputValue(value);
      }
      setSelectedOption(null);
    } else if (typeof value === 'object' && value !== null) {
      const label =
        value.savedAddressFullAddress ||
        value.fullAddress ||
        value.address ||
        value.label ||
        value.formatted_address ||
        '';
      if (label !== inputValue) {
        userTypingRef.current = false; // external update
        setInputValue(label);
      }
      setSelectedOption(value);
    }
  }, [value]);

  // Combine saved addresses with search results
  const combinedOptions = useMemo(() => {
    // Don't show any options if an address is already selected (edit mode)
    if (selectedOption) {
      return [];
    }

    if (!inputValue || inputValue.length < 2) {
      // Show saved addresses when empty or minimal input
      const savedOptions = [];

      // 1. Add API saved addresses first
      if (showSavedAddresses && savedAddressesFromApi.length > 0) {
        savedOptions.push({
          type: 'header',
          label: t('estimate.address.mySavedAddresses') || 'Saved Addresses',
        });
        savedAddressesFromApi.slice(0, 5).forEach((addr) => {
          savedOptions.push({
            ...addr,
            type: 'api-saved',
            address: addr.savedAddressFullAddress || addr.fullAddress || addr.label || addr.address,
            label: addr.savedAddressLabel || addr.addressLabel || addr.label,
            latitude: parseFloat(addr.savedAddressLatitude || addr.latitude) || null,
            longitude: parseFloat(addr.savedAddressLongitude || addr.longitude) || null,
          });
        });
      }

      if (showSavedAddresses && favorites.length > 0) {
        savedOptions.push({ type: 'header', label: t('estimate.address.favorites') });
        favorites.slice(0, 3).forEach((addr) => {
          savedOptions.push({
            ...addr,
            type: 'favorite',
            address: addr.address || addr.formatted_address,
          });
        });
      }

      if (showSavedAddresses && recents.length > 0) {
        savedOptions.push({ type: 'header', label: t('estimate.address.recent') });
        recents.slice(0, 3).forEach((addr) => {
          savedOptions.push({
            ...addr,
            type: 'recent',
            address: addr.address || addr.formatted_address,
          });
        });
      }

      return savedOptions;
    }

    // Always show all API saved addresses (not filtered by search) - similar to bulk booking
    const apiSavedOptions =
      showSavedAddresses && savedAddressesFromApi.length > 0
        ? savedAddressesFromApi.map((addr) => ({
            ...addr,
            type: 'api-saved',
            address: addr.savedAddressFullAddress || addr.fullAddress || addr.address,
            label: addr.savedAddressLabel || addr.addressLabel || addr.label,
            latitude: parseFloat(addr.savedAddressLatitude || addr.latitude) || null,
            longitude: parseFloat(addr.savedAddressLongitude || addr.longitude) || null,
          }))
        : [];

    // Filter other saved addresses (favorites/recents) by search term
    const matchingFavorites = favorites
      .filter((addr) => {
        const addrStr = (addr.address || addr.formatted_address || '').toLowerCase();
        return addrStr.includes(inputValue.toLowerCase());
      })
      .map((addr) => ({
        ...addr,
        type: 'favorite',
        address: addr.address || addr.formatted_address,
      }));

    const matchingRecents = recents
      .filter((addr) => {
        const addrStr = (addr.address || addr.formatted_address || '').toLowerCase();
        return addrStr.includes(inputValue.toLowerCase());
      })
      .map((addr) => ({
        ...addr,
        type: 'recent',
        address: addr.address || addr.formatted_address,
      }));

    const saved = [...matchingFavorites, ...matchingRecents];

    // API search results
    const apiOptions = options.map((opt) => ({ ...opt, type: 'search' }));

    // Combine: API saved addresses always shown, then other saved + search results
    const result = [];

    if (apiSavedOptions.length > 0) {
      result.push({
        type: 'header',
        label: t('estimate.address.mySavedAddresses') || 'Saved Addresses',
      });
      result.push(...apiSavedOptions);
    }

    if (saved.length > 0) {
      result.push({ type: 'header', label: t('estimate.address.savedAddresses') });
      result.push(...saved);
    }

    if (apiOptions.length > 0) {
      result.push({ type: 'header', label: t('estimate.address.searchResults') });
      result.push(...apiOptions);
    }

    return result.length > 0 ? result : [];
  }, [
    inputValue,
    options,
    favorites,
    recents,
    showSavedAddresses,
    savedAddressesFromApi,
    selectedOption,
  ]);

  // Fetch suggestions with debounce — only when user is actually typing
  useEffect(() => {
    let active = true;
    if (!inputValue || inputValue.length < 2 || !userTypingRef.current) {
      setOptions([]);
      return undefined;
    }
    setLoading(true);
    const timeout = setTimeout(async () => {
      if (!userTypingRef.current) {
        setLoading(false);
        return;
      }
      const results = await fetchAddressSuggestions(inputValue);
      if (active) {
        setOptions(results || []);
        if (results && results.length > 0) setOpen(true);
      }
      setLoading(false);
    }, 350);

    return () => {
      active = false;
      clearTimeout(timeout);
    };
  }, [inputValue]);

  const handleClear = () => {
    userTypingRef.current = false;
    setInputValue('');
    setSelectedOption(null);
    onChange && onChange('');
    setOptions([]);
    setOpen(false);
  };

  const handleSelectAddress = (addressObj) => {
    // For saved addresses, use full address; otherwise use label
    const val =
      addressObj.savedAddressFullAddress ||
      addressObj.fullAddress ||
      addressObj.address ||
      addressObj.label ||
      addressObj.formatted_address;
    console.log('[AddressInput] selected address', addressObj);
    userTypingRef.current = false;
    setInputValue(val);
    setSelectedOption(addressObj);
    onChange && onChange(val, addressObj);
    setOpen(false);
    setOptions([]);

    // Add to recents if not already a favorite
    if (addressObj.type !== 'favorite' && addressObj.type !== 'header') {
      dispatch(
        addRecent({
          address: val,
          formatted_address: val,
          latitude: addressObj.latitude,
          longitude: addressObj.longitude,
        })
      );
    }
  };

  // Save address to API and Redux
  const handleSaveAsFavorite = useCallback(
    async (e) => {
      e.stopPropagation();
      if (!inputValue) return;

      setSaving(true);
      try {
        // Try to save to API first
        const apiConfig = buildApiConfig(
          endPointEnums.CREATE_SAVED_ADDRESS,
          {},
          {
            addressLabel: inputValue.slice(0, 50),
            fullAddress: inputValue,
            addressType: 'PICKUP',
            city: selectedOption?.city || '',
            state: selectedOption?.state || '',
            pincode: selectedOption?.pincode || '',
            latitude: selectedOption?.latitude || null,
            longitude: selectedOption?.longitude || null,
          }
        );
        await makeApiCall(apiConfig);

        // Also save to Redux for immediate access
        dispatch(
          addFavorite({
            address: inputValue,
            formatted_address: inputValue,
          })
        );

        setSnackbar({
          open: true,
          message: t('estimate.address.addressSaved'),
          severity: 'success',
        });
      } catch (error) {
        console.error('Failed to save address to API:', error);
        // Still save locally even if API fails
        dispatch(
          addFavorite({
            address: inputValue,
            formatted_address: inputValue,
          })
        );
        setSnackbar({ open: true, message: t('estimate.address.savedLocally'), severity: 'info' });
      } finally {
        setSaving(false);
      }
    },
    [inputValue, dispatch, makeApiCall]
  );

  const handleSavedMenuOpen = (event) => {
    setSavedMenuAnchor(event.currentTarget);
  };

  const handleSavedMenuClose = () => {
    setSavedMenuAnchor(null);
  };

  const handleSelectFromMenu = (addr) => {
    const val = addr.address || addr.formatted_address;
    setInputValue(val);
    setSelectedOption(addr);
    onChange && onChange(val, addr);
    handleSavedMenuClose();
  };

  const hasSavedAddresses = favorites.length > 0 || recents.length > 0;

  return (
    <Box sx={{ position: 'relative', backgroundColor: '#fff' }}>
      <Autocomplete
        freeSolo
        open={open && combinedOptions.length > 0}
        onOpen={() => {
          if (inputValue && !selectedOption) {
            setOpen(true);
          }
        }}
        onClose={() => {
          setOpen(false);
          setOptions([]);
        }}
        options={combinedOptions}
        getOptionLabel={(opt) => {
          if (typeof opt === 'string') return opt;
          if (opt.type === 'header') return '';
          return (
            opt.savedAddressFullAddress ||
            opt.fullAddress ||
            opt.address ||
            opt.label ||
            opt.formatted_address ||
            ''
          );
        }}
        getOptionDisabled={(opt) => opt.type === 'header'}
        filterOptions={(x) => x}
        value={selectedOption}
        inputValue={inputValue}
        onInputChange={(e, v, reason) => {
          if (reason === 'input') {
            userTypingRef.current = true; // user is actively typing
            setInputValue(v);
            setSelectedOption(null);
            onChange && onChange(v);
            if (v && v.length >= 2) setOpen(true);
            else setOpen(false);
          } else if (reason === 'clear') {
            handleClear();
          }
          // ignore 'reset' reason since we manage selectedOption separately
        }}
        onChange={(e, v) => {
          if (!v) {
            handleClear();
            return;
          }
          if (typeof v === 'string') {
            // user typed something that matches existing option or free text
            setInputValue(v);
            setSelectedOption(null);
            onChange && onChange(v);
          } else if (v.type !== 'header') {
            handleSelectAddress(v);
          }
        }}
        loading={loading}
        renderOption={(props, option) => {
          if (option.type === 'header') {
            return (
              <Box
                key={option.label}
                sx={{
                  px: 2,
                  py: 1,
                  backgroundColor: '#f8fafc',
                  borderBottom: `1px solid ${COLORS.border}`,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: COLORS.textSecondary,
                    textTransform: 'uppercase',
                    letterSpacing: '0.8px',
                    fontSize: '0.7rem',
                  }}
                >
                  {option.label}
                </Typography>
              </Box>
            );
          }

          return (
            <Box
              component="li"
              {...props}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 1.5,
                mx: 0.5,
                my: 0.25,
                borderRadius: 2,
                cursor: 'pointer',
                backgroundColor: '#fff',
                transition: 'all 0.15s ease',
                '&:hover': {
                  backgroundColor: '#f1f5f9',
                },
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor:
                    option.type === 'favorite'
                      ? '#fef3c7'
                      : option.type === 'recent'
                        ? '#e0e7ff'
                        : '#f3f4f6',
                  flexShrink: 0,
                }}
              >
                {option.type === 'favorite' ? (
                  <StarIcon sx={{ color: '#f59e0b', fontSize: 18 }} />
                ) : option.type === 'recent' ? (
                  <HistoryIcon sx={{ color: '#4f46e5', fontSize: 18 }} />
                ) : (
                  <LocationIcon sx={{ color: COLORS.primary, fontSize: 18 }} />
                )}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: option.type === 'favorite' ? 600 : 500,
                    color: COLORS.textPrimary,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    lineHeight: 1.4,
                  }}
                >
                  {option.address || option.label || option.formatted_address}
                </Typography>
              </Box>
              {option.type === 'favorite' && (
                <Chip
                  size="small"
                  label="Saved"
                  sx={{
                    height: 20,
                    fontSize: '0.65rem',
                    backgroundColor: '#fef3c7',
                    color: '#92400e',
                  }}
                />
              )}
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            size="small"
            {...params}
            placeholder={label}
            fullWidth
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <LocationIcon sx={{ color: COLORS.textSecondary, fontSize: 18 }} />
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  {loading && <CircularProgress size={14} sx={{ mr: 1 }} />}
                  {inputValue && (
                    <>
                      <IconButton
                        onClick={handleSaveAsFavorite}
                        size="small"
                        title={t('estimate.address.saveAsFavorite')}
                        sx={{ color: '#f59e0b', mr: 0.5 }}
                      >
                        <StarBorderIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        onClick={handleClear}
                        size="small"
                        sx={{ color: COLORS.textSecondary, mr: 0.5 }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </>
                  )}
                  {!inputValue && hasSavedAddresses && showSavedAddresses && (
                    <IconButton
                      onClick={handleSavedMenuOpen}
                      size="small"
                      sx={{ color: COLORS.primary, mr: 0.5 }}
                      title={t('estimate.address.savedAddressesMenu')}
                    >
                      <SavedIcon fontSize="small" />
                    </IconButton>
                  )}
                  {params.InputProps.endAdornment}
                  {onUseCurrent && (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={onUseCurrent}
                        size="small"
                        sx={{
                          color: COLORS.primary,
                          '&:hover': { backgroundColor: `${COLORS.primary}10` },
                        }}
                      >
                        <MyLocationIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  )}
                </>
              ),
            }}
            sx={{
              minWidth: 280,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2.5,
                backgroundColor: COLORS.white,
                fontSize: '0.9rem',
                minHeight: 48,
                '& fieldset': {
                  borderColor: COLORS.border,
                  borderWidth: 1.5,
                },
                '&:hover fieldset': {
                  borderColor: COLORS.primary,
                },
                '&.Mui-focused fieldset': {
                  borderColor: COLORS.primary,
                  borderWidth: 2,
                },
              },
              '& input': {
                fontWeight: 500,
                color: COLORS.textPrimary,
                padding: '12px 14px',
                fontSize: '0.9rem',
              },
            }}
          />
        )}
        ListboxProps={{
          sx: {
            maxHeight: 360,
            p: 0.5,
            '& .MuiAutocomplete-option': {
              p: 0,
              borderRadius: 1.5,
              mx: 0.5,
              my: 0.25,
            },
          },
        }}
        PaperComponent={AutocompletePaperWrapper}
      />

      {/* simple preview of selected coordinates or full address for debugging/confirmation */}
      {/* preview: show selectedOption details if available, otherwise fall back to typed text once the dropdown closes */}
      {!open && (selectedOption || inputValue) && (
        <Box
          sx={{
            mt: 0.5,
            ml: 1,
            fontSize: '0.75rem',
            color: COLORS.textSecondary,
            wordBreak: 'break-word',
          }}
        >
          {selectedOption ? selectedOption.address || selectedOption.label || '' : inputValue}
          {selectedOption &&
            typeof selectedOption.latitude === 'number' &&
            typeof selectedOption.longitude === 'number' &&
            selectedOption.latitude &&
            selectedOption.longitude && (
              <>
                &nbsp;({selectedOption.latitude.toFixed(4)}, {selectedOption.longitude.toFixed(4)})
              </>
            )}
        </Box>
      )}

      {/* Show selected label as chip if available */}
      {selectedOption && (selectedOption.savedAddressLabel || selectedOption.label) && (
        <Chip
          label={selectedOption.savedAddressLabel || selectedOption.label}
          size="small"
          sx={{
            mt: 1,
            ml: 0.5,
            bgcolor: '#fef3c7',
            color: '#92400e',
            fontWeight: 600,
            fontSize: '0.75rem',
          }}
        />
      )}

      {/* Collapsible saved addresses section */}
      {showSavedAddresses && savedAddressesFromApi.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Box
            onClick={() => setExpandedSavedAddresses(!expandedSavedAddresses)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1,
              cursor: 'pointer',
              p: 0.75,
              borderRadius: 1,
              transition: 'bgcolor 200ms',
              '&:hover': {
                bgcolor: '#f0fdf4',
              },
            }}
          >
            <Typography
              sx={{
                fontSize: '0.7rem',
                fontWeight: 700,
                color: '#10b981',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                flex: 1,
              }}
            >
              Saved Addresses ({savedAddressesFromApi.length})
            </Typography>
            <ExpandMoreIcon
              sx={{
                fontSize: '1.2rem',
                color: '#10b981',
                transition: 'transform 200ms',
                transform: expandedSavedAddresses ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            />
          </Box>
          <Collapse in={expandedSavedAddresses} timeout="auto" unmountOnExit>
            <Stack spacing={0.75}>
              {savedAddressesFromApi.map((addr) => (
                <Paper
                  key={addr.savedAddressId || addr.addressId}
                  elevation={0}
                  onClick={() => handleSelectAddress(addr)}
                  sx={{
                    p: 1.5,
                    borderRadius: 1.5,
                    bgcolor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    cursor: 'pointer',
                    transition: 'all 200ms',
                    '&:hover': {
                      borderColor: '#10b981',
                      bgcolor: '#ecfdf5',
                      transform: 'translateX(2px)',
                    },
                  }}
                >
                  <Stack spacing={0.5}>
                    {(addr.savedAddressLabel || addr.addressLabel || addr.label) && (
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          color: '#10b981',
                        }}
                      >
                        {addr.savedAddressLabel || addr.addressLabel || addr.label}
                      </Typography>
                    )}
                    <Typography
                      sx={{
                        fontSize: '0.8rem',
                        color: '#475569',
                        lineHeight: 1.3,
                      }}
                    >
                      {addr.savedAddressFullAddress || addr.fullAddress || addr.address}
                    </Typography>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </Collapse>
        </Box>
      )}

      {/* Saved Addresses Menu */}
      <Menu
        anchorEl={savedMenuAnchor}
        open={Boolean(savedMenuAnchor)}
        onClose={handleSavedMenuClose}
        PaperProps={{
          sx: {
            width: 320,
            maxHeight: 400,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            borderRadius: 2,
          },
        }}
      >
        {favorites.length > 0 && (
          <>
            <Box sx={{ px: 2, py: 1, backgroundColor: COLORS.surface }}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: COLORS.textSecondary,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                ⭐ {t('estimate.address.favorites')}
              </Typography>
            </Box>
            {favorites.map((addr, idx) => (
              <MenuItem
                key={`fav-${idx}`}
                onClick={() => handleSelectFromMenu(addr)}
                sx={{ py: 1.5 }}
              >
                <ListItemIcon>
                  <StarIcon sx={{ color: '#f59e0b', fontSize: 18 }} />
                </ListItemIcon>
                <ListItemText
                  primary={addr.address || addr.formatted_address}
                  primaryTypographyProps={{
                    fontSize: '0.85rem',
                    fontWeight: 500,
                    noWrap: true,
                  }}
                />
              </MenuItem>
            ))}
          </>
        )}

        {recents.length > 0 && (
          <>
            {favorites.length > 0 && <Divider />}
            <Box sx={{ px: 2, py: 1, backgroundColor: COLORS.surface }}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: COLORS.textSecondary,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                🕐 {t('estimate.address.recent')}
              </Typography>
            </Box>
            {recents.map((addr, idx) => (
              <MenuItem
                key={`rec-${idx}`}
                onClick={() => handleSelectFromMenu(addr)}
                sx={{ py: 1.5 }}
              >
                <ListItemIcon>
                  <HistoryIcon sx={{ color: COLORS.textSecondary, fontSize: 18 }} />
                </ListItemIcon>
                <ListItemText
                  primary={addr.address || addr.formatted_address}
                  primaryTypographyProps={{
                    fontSize: '0.85rem',
                    fontWeight: 500,
                    noWrap: true,
                  }}
                />
              </MenuItem>
            ))}
          </>
        )}
      </Menu>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%', fontWeight: 600 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
